/*
 * Knuska collection info - the fold on the long copy.
 *
 * The element arrives folded from the server, so there is nothing to set up on
 * load and nothing that moves under a reader who is already down here. What is
 * left for script is the part markup cannot know: whether this collection's
 * copy is actually longer than the fold, and the open and shut of it.
 *
 * Everything is written as attributes on the element - data-state and open -
 * so the stylesheet keeps every decision about how the two states look, and
 * this file keeps only the decision about which one applies.
 */
class KnuskaCollectionInfoMore extends HTMLElement {
  connectedCallback() {
    this.clip = this.querySelector('.knuska-ci__clip');
    this.button = this.querySelector('.knuska-ci__more-btn');
    this.label = this.querySelector('.knuska-ci__more-label');

    if (!this.clip || !this.button) return;

    this.copy = this.clip.firstElementChild;
    this.labels = {
      more: this.dataset.labelMore || 'Lees meer',
      less: this.dataset.labelLess || 'Lees minder',
    };

    this.button.addEventListener('click', () => this.toggle());

    /*
     * The measurement is only meaningful once the fonts the copy is set in have
     * loaded - a fallback face can be a line or two out over a few screens of
     * text, which is the difference between a collection getting a button it
     * does not need and losing one it does.
     */
    this.measure();
    if (document.fonts) document.fonts.ready.then(() => this.measure());

    /*
     * Rotating a phone rewraps the copy, and crossing the breakpoint hands the
     * text a column of its own where the fold does not apply at all. Both come
     * through as a resize; the stylesheet handles the breakpoint, this only has
     * to keep the long-or-short answer honest at the new width.
     */
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.copy);
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  /*
   * The fold height is the same custom property the stylesheet clips to, so
   * the two cannot disagree - and it is the merchant's setting, in px.
   */
  get foldHeight() {
    const value = parseFloat(getComputedStyle(this).getPropertyValue('--ci-clip'));
    return Number.isFinite(value) ? value : 240;
  }

  measure() {
    if (this.hasAttribute('open')) return;

    // A button to reveal one more line is worse than the line being cut, so
    // the copy has to overrun the fold by enough to be worth the tap.
    const long = this.copy.offsetHeight > this.foldHeight + 48;
    this.dataset.state = long ? 'long' : 'short';
  }

  toggle() {
    if (this.hasAttribute('open')) this.close();
    else this.open();
  }

  open() {
    // Measured now rather than kept, because the copy is only certainly at its
    // full height in the layout it is in at this moment.
    this.style.setProperty('--ci-full', `${this.copy.offsetHeight}px`);
    this.setAttribute('open', '');
    this.button.setAttribute('aria-expanded', 'true');
    if (this.label) this.label.textContent = this.labels.less;

    /*
     * Once it has finished opening the height stops being a number: an image
     * that decodes late, or a font that swaps, should lengthen the copy rather
     * than be cut off by the height it happened to have when it was opened.
     */
    this.clip.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName === 'max-height' && this.hasAttribute('open')) {
          this.clip.style.maxHeight = 'none';
        }
      },
      { once: true }
    );
  }

  close() {
    /*
     * Back to a number first, and only then to the fold - a transition needs
     * two lengths, and 'none' is not one. The read forces the browser to settle
     * on the first before the second is set, which is what makes it animate
     * rather than snap.
     */
    this.clip.style.maxHeight = `${this.copy.offsetHeight}px`;
    void this.clip.offsetHeight;

    this.removeAttribute('open');
    this.clip.style.maxHeight = '';

    this.button.setAttribute('aria-expanded', 'false');
    if (this.label) this.label.textContent = this.labels.more;

    /*
     * Shutting several screens of text pulls the page out from under the
     * reader, who would otherwise land somewhere in the footer with no idea
     * what happened. Only when the section has already gone past the top:
     * while it is still on screen, closing it moves nothing they can see.
     */
    if (this.getBoundingClientRect().top < 0) {
      const section = this.closest('.knuska-collection-info') || this;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      section.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
    }
  }
}

if (!customElements.get('knuska-collection-info-more')) {
  customElements.define('knuska-collection-info-more', KnuskaCollectionInfoMore);
}
